import { useCallback, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import Map from 'components/map/map';
import PolygonArea from 'components/map/polygon-area';
import { Button, Checkbox, InlineNotification, Tag } from '@carbon/react';
import { Checkmark, Edit } from '@carbon/react/icons';
import { Field } from 'components/field';
import { useCalculateMapDimensions } from 'components/map/utils';
import { ActivityFeedPanel } from 'components/activity-feed-panel';
import { format2 } from 'utils/number';
import { capitalizeFirstLetter } from 'utils/strings';
import { resolveColorByUwiMembership } from 'pages/pre-drill-wellbore-workspace/utils';
import {
  areaColorByUwiMapAtom,
  areaColorRulesAtom,
  areaWellFiltersAtom,
  mapConfigAtom,
  mapRefAtom,
} from './atoms';
import { AreaScopedWellsLayer } from './area-scoped-wells-layer';
import { AreaHighlightedWellLayer } from './area-highlighted-well-layer';
import { useAreaColorFacets } from './use-area-color-facets';
import { calculateGeometryBounds } from './utils';
import { formatDate } from '../utils';
import { AreaSettingsPanel } from './area-settings-panel';
import { AreaScopedDatasets } from './area-scoped-datasets';
import { AreaDatasetMapLayers } from './area-dataset-map-layers';
import {
  CrossSectionMapTool,
  CrossSectionPanel,
  CrossSectionSettingsPanel,
} from 'components/cross-section';
import { allDatasetAtoms } from 'pages/map-query/atoms';
import {
  useAreaDatasetStore,
  useCrossSectionSection,
  useCrossSectionWellColors,
  useWellToWellZoom,
} from './hooks';
import { wellFilterQueryParams } from './well-filters';
import {
  DEFAULT_SECTION_VISIBILITY,
  type SectionKey,
} from './section-visibility';
import type { AreaGeometry } from 'types/area-geometry';
import styles from './area-detail-body.module.scss';

export type { AreaGeometry };

// Fallback fill for an area with no colour of its own — most of them, since
// `display_color` is only set by areas created through the builder.
const DEFAULT_AREA_COLOR = '#EB6200';

// The area's own attributes, in the order the mock's grid reads them.
const AreaGeometryDetails = ({
  areaDetails,
  sharedField,
}: {
  areaDetails: AreaGeometry;
  /** Replaces the Shared value while the card is being edited. */
  sharedField?: JSX.Element;
}): JSX.Element => (
  <div className={styles.detailsFields}>
    <Field label="Category" value={areaDetails.category} />
    <Field label="Created By" value={areaDetails.createdBy} />
    <Field
      label="Display Name"
      value={areaDetails.displayName || areaDetails.name}
    />
    <Field label="Purpose" value={areaDetails.purpose} />
    {/* Read as UTC like the catalog — `parseISO` shifts into the browser's zone, so a
        late-evening record would read a day early here and a day later in the list. */}
    <Field label="Created At" value={formatDate(areaDetails.createdAt)} />
    <Field label="Active" value={areaDetails.active ? 'Yes' : 'No'} />
    <Field label="Updated By" value={areaDetails.updatedBy} />
    <Field label="Data Source" value={areaDetails.dataSource} />
    <Field label="Gross Acres" value={format2(areaDetails.grossAcres)} />
    <Field label="Updated At" value={formatDate(areaDetails.updatedAt)} />
    {/* Last, as the mock has it: it is the one field that becomes a control, so it
        reads as the row you act on rather than as another attribute. */}
    {sharedField ? (
      <div>
        <p className={styles.fieldLabel}>Shared</p>
        {sharedField}
      </div>
    ) : (
      <Field label="Shared" value={areaDetails.shared ? 'Yes' : 'No'} />
    )}
  </div>
);

// The Area Details card. Read-only wherever the area cannot be edited; inside an
// unfrozen workspace the pencil turns the map flag into a checkbox, as the mock has it,
// and the tick writes it. Only that one flag is editable in this story.
const AreaDetailsCard = ({
  area,
  onSharedChange,
  saving,
  error,
}: {
  area: AreaGeometry;
  onSharedChange?: (shared: boolean) => void;
  saving: boolean;
  error: string | null;
}): JSX.Element => {
  const [editing, setEditing] = useState(false);
  // The pending value while the card is open for editing; the area is the truth
  // otherwise, so leaving edit mode discards nothing that was written.
  const [shared, setShared] = useState(area.shared);

  const open = () => {
    setShared(area.shared);
    setEditing(true);
  };

  // Turning it off opens the warning panel, which owns the write and closes the card
  // once it lands; turning it on writes straight away. Either way the card stops
  // editing, since the next render reads the area again.
  const commit = () => {
    setEditing(false);
    if (shared !== area.shared) {
      onSharedChange?.(shared);
    }
  };

  return (
    <>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleGroup}>
          <h3 className={styles.cardTitle}>Area Details</h3>
          {/* Someone marked this shape correct and promoted it, so a GIS import holds its
              change in a draft for review instead of overwriting the area. */}
          {area.frozenAgainstGisReingest && (
            <span title="GIS imports won't overwrite this shape; their changes wait in a draft workspace for review.">
              <Tag type="green" size="sm">
                Marked correct
              </Tag>
            </span>
          )}
        </div>
        {onSharedChange && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            iconDescription={
              editing ? 'Save area details' : 'Edit area details'
            }
            tooltipPosition="left"
            renderIcon={editing ? Checkmark : Edit}
            disabled={saving}
            onClick={editing ? commit : open}
          />
        )}
      </div>
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not update the area"
          subtitle={error}
        />
      )}
      <AreaGeometryDetails
        areaDetails={area}
        sharedField={
          editing ? (
            <Checkbox
              id="area-details-shared"
              labelText="Promote to map"
              checked={shared}
              disabled={saving}
              onChange={(_event, { checked }) => setShared(checked)}
            />
          ) : undefined
        }
      />
    </>
  );
};

// The area's activity feed — versions and events in one list, the same drawer the AFE,
// Entity and Lexicon detail pages use.
const ActivityContent = ({
  areaGeometry,
}: {
  areaGeometry: AreaGeometry;
}): JSX.Element => {
  // The area's own model and id are the fallback, so a response served before the
  // resource existed opens the drawer rather than taking the page down with it.
  const feed = areaGeometry.resources?.activityFeed?.body;

  return (
    <ActivityFeedPanel
      modelType={feed?.modelType ?? 'AreaGeometry'}
      modelId={feed?.modelId ?? areaGeometry.id}
      title="Activity"
      subtitle={areaGeometry.displayName || areaGeometry.name}
      commentsHref={areaGeometry.resources?.comments?.href ?? ''}
    />
  );
};

/**
 * The Map card's title, with Edit Area Geometry beside it where the area can be
 * edited (inside an unfrozen workspace, Epic 2). A shape the server holds back — a Unit
 * (managed through GIS) or a structure-grid extent — keeps the button, disabled, with
 * the server's reason beneath, so the user knows why it can't be edited here.
 *
 * @param {object} props - The props.
 * @param {() => void} [props.onEditGeometry] - Opens the editor, when offered.
 * @param {string | null} [props.blockedReason] - Why the shape can't be edited, if it can't.
 * @return {JSX.Element} The title row.
 */
function MapCardTitle({
  onEditGeometry,
  blockedReason = null,
}: {
  onEditGeometry?: () => void;
  blockedReason?: string | null;
}): JSX.Element {
  if (!onEditGeometry) return <h3 className={styles.cardTitle}>Map</h3>;
  return (
    <>
      <div className={styles.cardTitleRow}>
        <h3 className={styles.cardTitle}>Map</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Edit}
          disabled={blockedReason !== null}
          onClick={onEditGeometry}
        >
          Edit Area Geometry
        </Button>
      </div>
      {blockedReason && (
        <p className={styles.editBlocked}>
          {capitalizeFirstLetter(blockedReason)}.
        </p>
      )}
    </>
  );
}

// A settings rail beside the area's cards — attributes, purpose, boundary, wells — plus
// the activity drawer. Shared with the Area Workspace, which reads it under its header.
export interface AreaDetailBodyProps {
  area: AreaGeometry;
  panelOpen?: boolean;
  // Writes the map flag. Passed only where the area can be edited — inside an
  // unfrozen workspace — so its absence is what makes the card read-only everywhere
  // else, including the area detail page.
  onSharedChange?: (shared: boolean) => void;
  /** True while that write is in flight. */
  sharedSaving?: boolean;
  /** Why the last flag write was refused, or null. */
  sharedError?: string | null;
  /**
   * Opens Edit Area Geometry (Epic 2). Passed only inside an unfrozen workspace,
   * so the details page and a promoted workspace never offer it.
   */
  onEditGeometry?: () => void;
}

export const AreaDetailBody = ({
  area,
  panelOpen = true,
  onSharedChange,
  sharedSaving = false,
  sharedError = null,
  onEditGeometry,
}: AreaDetailBodyProps): JSX.Element => {
  const containerRef = useRef(null);
  const [visibility, setVisibility] = useState(DEFAULT_SECTION_VISIBILITY);
  const toggleSection = useCallback(
    (section: SectionKey) =>
      setVisibility((current) => ({
        ...current,
        [section]: !current[section],
      })),
    []
  );
  // Story 7: the Cross Section section keeps the tool armed (see the hook).
  const crossSection = useCrossSectionSection(
    visibility.map,
    setVisibility,
    toggleSection,
    area.geometry
  );
  const shownSections = { ...visibility, crossSection: crossSection.on };

  // Area Scoped Datasets: Map Query's tables in a store of their own, bounded by
  // the area and, on Wells, by the rail's filters. The map's layers read it too.
  const wellFilters = useAtomValue(areaWellFiltersAtom);
  const wellParams = useMemo(
    () => wellFilterQueryParams(wellFilters),
    [wellFilters]
  );
  const mapRef = useAtomValue(mapRefAtom);
  const datasetStore = useAreaDatasetStore(area.geometry, wellParams, mapRef);
  const wellToWellZoomedOut = useWellToWellZoom(mapRef);
  // The rail's Wells count, from the same search the Wells tab shows.
  const wellsSearch = useAtomValue(allDatasetAtoms.wells.searchAtom, {
    store: datasetStore,
  });
  const totalCount = wellsSearch.data?.totalCount ?? null;

  // Colour-by: the modal's value lists come from a facet call scoped to this
  // boundary, and the paint map is grouped from that call's wells.
  const colorFacets = useAreaColorFacets(area.geometry);
  const colorRules = useAtomValue(areaColorRulesAtom);
  useCrossSectionWellColors(colorRules, colorFacets.wells);
  // The rail's wells toggle governs the coloured overlay as well as the shared
  // layer, so switching wells off clears both rather than leaving ours drawing.
  const wellsOnMap = useAtomValue(mapConfigAtom).wellsV2.checked;
  const setColorByUwiMap = useSetAtom(areaColorByUwiMapAtom);
  const handleColorSelectionChange = useCallback(
    (categoryId: string, values: string[]) => {
      if (values.length === 0) {
        return;
      }
      setColorByUwiMap((current) => {
        const next = { ...current };
        values.forEach((value) => {
          next[`${categoryId}:${value}`] = resolveColorByUwiMembership(
            colorFacets.wells,
            categoryId,
            value
          );
        });
        return next;
      });
    },
    [colorFacets.wells, setColorByUwiMap]
  );

  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: calculateGeometryBounds(area.geometry),
    containerRef,
  });

  // Back to the area's own extent, the view the map opens on.
  const handleResetZoom = useCallback(() => {
    mapRef?.easeTo({ center: [longitude, latitude], zoom: zoom - 2 });
  }, [mapRef, longitude, latitude, zoom]);

  return (
    <div className={styles.layout}>
      {panelOpen && (
        <AreaSettingsPanel
          visibility={shownSections}
          onToggleSection={crossSection.toggle}
          wellCount={totalCount}
          colorFacets={colorFacets}
          onColorSelectionChange={handleColorSelectionChange}
        />
      )}

      <div className={styles.content}>
        {visibility.details && (
          <section className={styles.card}>
            <AreaDetailsCard
              area={area}
              onSharedChange={onSharedChange}
              saving={sharedSaving}
              error={sharedError}
            />
          </section>
        )}

        {visibility.purpose && (
          <section className={styles.card}>
            <h3 className={styles.cardTitle}>Purpose</h3>
            {area.purpose ? (
              <p className={styles.purpose}>{area.purpose}</p>
            ) : (
              <p className={styles.empty}>No purpose notes ingested.</p>
            )}
          </section>
        )}

        {visibility.map && (
          <section className={styles.card}>
            <MapCardTitle
              onEditGeometry={onEditGeometry}
              blockedReason={
                area.geometryEditable === false
                  ? (area.geometryEditBlockedReason ??
                    'this area cannot be edited here')
                  : null
              }
            />
            {/* Ref on the sized box: the map is absolutely positioned, so a bare
                wrapper measures 0 high and `fitBounds` returns NaN. */}
            <div className={styles.mapContainer} ref={containerRef}>
              <Map
                className={styles.map}
                longitude={longitude}
                latitude={latitude}
                zoom={zoom - 2}
                mapConfigAtom={mapConfigAtom}
                refAtom={mapRefAtom}
                showControls
                showMapFilters
                onResetZoom={handleResetZoom}
              >
                {/* An area's own colour where it has one, so it matches every other
                    map; GIS-imported rows keep the default fill. */}
                <PolygonArea
                  id={area.id}
                  geometry={area.geometry}
                  color={area.displayColor || DEFAULT_AREA_COLOR}
                />
                {/* The area's wells in the legend's colours, and a rule's where one
                    matches — over the base layer, as Type Curve paints its own. */}
                <AreaScopedWellsLayer
                  colorRules={colorRules}
                  wells={colorFacets.wells}
                  visible={wellsOnMap}
                />
                {/* A halo under the well whose row is selected in the table. */}
                <AreaHighlightedWellLayer />
                <AreaDatasetMapLayers store={datasetStore} />
                <CrossSectionMapTool />
              </Map>
            </div>
            {wellToWellZoomedOut && (
              <InlineNotification
                className={styles.mapNotice}
                kind="info"
                lowContrast
                hideCloseButton
                title="Zoom in to pick wells."
                subtitle="Vertical wells for Well to Well show at closer zoom levels."
              />
            )}
          </section>
        )}

        {crossSection.on && (
          <section className={styles.crossSectionCard}>
            <CrossSectionPanel
              defaultCollectionId={
                area.defaultStructureGridCollectionId ??
                area.structureGridCollectionId ??
                null
              }
            />
          </section>
        )}

        {crossSection.armed && (
          <div className={styles.crossSectionSettings}>
            <CrossSectionSettingsPanel />
          </div>
        )}

        {visibility.wells && (
          <section className={styles.card}>
            <h3 className={styles.cardTitle}>Area Scoped Datasets</h3>
            <AreaScopedDatasets store={datasetStore} />
          </section>
        )}

        <ActivityContent areaGeometry={area} />
      </div>
    </div>
  );
};
